import { addHours } from "date-fns";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { PersistOptions } from "zustand/middleware";
import type { AggregateOptions } from "~/components/charts/aggregateOption";
import type { DateRange } from "~/utils/dateUtil";

export type NodeTableChartState = {
  aggregateOptions: AggregateOptions;
  range: DateRange;
};

export const defaultChartState: NodeTableChartState = {
  aggregateOptions: {
    fillEmpty: false,
    fillWith: "forward",
    functions: ["last"],
    windowSize: "15m",
  },
  range: {
    from: addHours(new Date(), -24),
    to: new Date(),
  },
};

export interface UiNodeState {
  tableApplicationFilter: string[] | null;
  tableNodeTypeFilter: string[] | null;
  tableActiveStatusFilter: string | null;
  tableSelectedRowIds: Record<string, boolean>;
  tableSearchValue: string | null;
  tableChartState: NodeTableChartState;

  setTableApplicationFilter: (filterValues: string[] | string | null) => void;
  setTableNodeTypeFilter: (filterValues: string[] | string | null) => void;
  setTableActiveStatusFilter: (filterValue: string[] | string | null) => void;
  setTableSelectedRowIds: (rowIds: Record<string, boolean>) => void;
  setTableSearchValue: (searchValue: string | null) => void;
  setTableChartState: (chartState: NodeTableChartState) => void;

  resetChartState: () => void;
  reset: () => void;
}

const useUiNodeState = create<UiNodeState>()(
  persist(
    (set, _) => ({
      tableActiveStatusFilter: null,
      tableApplicationFilter: null,
      tableNodeTypeFilter: null,
      tableSelectedRowIds: {},
      tableSearchValue: null,
      tableChartState: defaultChartState,

      setTableActiveStatusFilter: (filterValue: string[] | string | null) =>
        set(() => ({ tableActiveStatusFilter: filterValue as string })),
      setTableApplicationFilter: (filterValues: string[] | string | null) =>
        set(() => ({ tableApplicationFilter: filterValues as string[] })),
      setTableNodeTypeFilter: (filterValues: string[] | string | null) =>
        set(() => ({ tableNodeTypeFilter: filterValues as string[] })),
      setTableSelectedRowIds: (rowIds: Record<string, boolean>) =>
        set(() => ({ tableSelectedRowIds: rowIds })),
      setTableSearchValue: (searchValue: string | null) =>
        set(() => ({ tableSearchValue: searchValue })),
      setTableChartState: (chartState: NodeTableChartState) =>
        set(() => ({ tableChartState: chartState })),
      resetChartState: () =>
        set(() => ({ tableChartState: defaultChartState })),

      reset: () =>
        set(() => ({
          tableActiveStatusFilter: null,
          tableApplicationFilter: null,
          tableNodeTypeFilter: null,
          tableSelectedRowIds: {},
          tableSearchValue: null,
          tableChartState: defaultChartState,
        })),
    }),
    {
      name: "ui-node-state",
    } as PersistOptions<UiNodeState>,
  ),
);

export default useUiNodeState;
